import { Component, ElementRef, HostListener, inject, output, OnInit, signal, DestroyRef } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from '../../auth/services/authentication.service';
import { TokenStorageService } from '../../auth/services/token-storage.service';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/chapter-setup/add-chapter': 'Add Chapter',
  '/chapter-setup/chapter-list': 'Chapter List',
  '/question-manager/add-question': 'Add Question',
  '/question-manager/questions': 'Questions',
  '/exam-manager/add-exam': 'Create Exam',
  '/exam-manager/exam-list': 'Exam List',
  '/exam-manager/exam-questions': 'Exam Preview',
  '/exam-manager/assign-exam': 'Assign Exam',
  '/exam-manager/examPapers': 'Exam Papers',
  '/exam-manager/markExam': 'Exam Marking',
  '/teacher-manager/add-teacher': 'Add User',
  '/users': 'Users',
  '/sync-programs-subjects': 'Programs & Subjects',
  '/system-settings': 'Settings',
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [MatToolbarModule, MatIconModule, MatButtonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  private authService = inject(AuthenticationService);
  private tokenStorage = inject(TokenStorageService);
  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  menuClick = output<void>();
  userName = signal('');
  userEmail = signal('');
  panelOpen = signal(false);
  pageTitle = signal('Dashboard');

  ngOnInit(): void {
    const session = this.tokenStorage.getUserSession();
    this.userName.set(this.tokenStorage.getFullName() || session?.username || '');
    this.userEmail.set(session?.username ?? '');

    this.pageTitle.set(this.getTitleForUrl(this.router.url));

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((e) => {
      this.pageTitle.set(this.getTitleForUrl((e as NavigationEnd).urlAfterRedirects));
    });
  }

  private getTitleForUrl(url: string): string {
    const path = url.split('?')[0];
    if (ROUTE_TITLES[path]) return ROUTE_TITLES[path];
    const prefix = Object.keys(ROUTE_TITLES).find(k => path.startsWith(k + '/'));
    return prefix ? ROUTE_TITLES[prefix] : 'Exam Portal';
  }

  onMenuClick(): void {
    this.menuClick.emit();
  }

  togglePanel(event: MouseEvent): void {
    event.stopPropagation();
    this.panelOpen.update(v => !v);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.panelOpen.set(false);
  }

  logout(): void {
    this.authService.logOut();
  }
}
